import { R4 } from "@ahryman40k/ts-fhir-types";
import { isUUID } from "validator";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import {
  deleteDocument,
  fetchDocumentReference,
  fetchDocuments,
  updateDocumentStatus,
} from "@/api/documents";
import config from "@/config";
import { DirectNodeChild, Tree, UnauthorizedError } from "@/global";

import Store from "../index";
import AccountModule, { LocalStorageItemKeys } from "./account";

const accountState = getModule(AccountModule);

const authHeaderFunction = async function (): Promise<string | null> {
  try {
    return accountState.getAuthenticationHeader();
  } catch (error) {
    return null;
  }
};

function normalizeDocumentId(id?: string) {
  return id && isUUID(id) ? btoa(`${id}@urn:oid:${config.homeCommunity.id}`) : id;
}

const mapLocalEntries = function (
  lists: R4.IList[],
  localEntries: Map<string, IDocumentReferenceExtended>
): Map<string, IDocumentReferenceExtended> {
  lists.forEach((list) => {
    if (list.entry && list.entry.length > 0) {
      const ref = list.entry![0].item.reference;
      if (ref) {
        const split = ref.split("/");
        const id = split[split.length - 1];

        const homeCommunityID =
          list.extension
            ?.find(
              (ext) =>
                ext.url == "https://api.phellowseven.com/fhir/StructureDefinition/homeCommunityId"
            )
            ?.valueString?.replace("urn:oid:", "") || config.homeCommunity.id;

        const docRef = localEntries.get(homeCommunityID + "-" + id);
        if (docRef) {
          docRef.submissionSet = list;
        }
      }
    }
  });

  return localEntries;
};

function getHomeCommunityId(entry: IDocumentReferenceExtended) {
  const homeCommunityID =
    entry.extension
      ?.find(
        (ext) => ext.url == "https://api.phellowseven.com/fhir/StructureDefinition/homeCommunityId"
      )
      ?.valueString?.replace("urn:oid:", "") || config.homeCommunity.id;

  return homeCommunityID;
}

@Module({
  dynamic: true,
  store: Store,
  name: "document",
  namespaced: true,
})
export default class DocumentModule extends VuexModule {
  isLoading: boolean = false;
  entries: Map<string, IDocumentReferenceExtended> = new Map();
  entriesVersion: number = 1;
  leafEntries: IDocumentReferenceExtended[] = [];
  relationshipTree: Tree = new Tree(null);
  count: number = +(window.localStorage.getItem(LocalStorageItemKeys.DocumentCount) || 0);

  private lists: R4.IList[] = [];
  private references: R4.IDocumentReference[] = [];

  @Action({ rawError: true })
  async getDocument(id: string): Promise<IDocumentReferenceExtended> {
    const filtered = this.entries.get(id);

    if (filtered) {
      return filtered;
    }

    throw Error("No Document with this ID");
  }

  // @Action({ rawError: true })
  // async getDocumentForLogicalUUID(uuid: string): Promise<IDocumentReferenceExtended> {
  //   const filtered = this.entries.get(id);

  //   if (filtered) {
  //     return filtered;
  //   }

  //   throw Error("No Document with this ID");
  // }

  @Action
  async getDocuments(ids: string[]): Promise<IDocumentReferenceExtended[]> {
    const collected: IDocumentReferenceExtended[] = [];

    ids.forEach((id) => {
      const entry = this.entries.get(id);
      if (entry) {
        collected.push(entry);
      }
    });

    return collected;
  }

  @Mutation
  resetCount() {
    this.count = 0;
    window.localStorage.setItem(LocalStorageItemKeys.DocumentCount, `${this.count}`);
  }

  @Mutation
  addCount(count: number) {
    this.count += count;
    window.localStorage.setItem(LocalStorageItemKeys.DocumentCount, `${this.count}`);
  }

  @Mutation
  clearDocuments() {
    this.entries = new Map();
    this.entriesVersion = 1;
    this.relationshipTree = new Tree(null);
    this.leafEntries = [];
  }

  @Mutation
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  @Mutation
  setRelationshipTree(tree: Tree) {
    this.relationshipTree = tree;
  }

  @Mutation
  setLeafEntries(leafEntries: IDocumentReferenceExtended[]) {
    this.leafEntries = leafEntries;
  }

  @Mutation
  setLists(lists: R4.IList[]) {
    this.lists = lists;
  }

  @Mutation
  setReferences(references: R4.IDocumentReference[]) {
    this.references = references;
  }

  @Mutation
  addReference(reference: R4.IDocumentReference) {
    this.references.push(reference);
  }

  @Mutation
  updateReference(reference: R4.IDocumentReference) {
    const normalizedDocumentId = normalizeDocumentId(reference.id);
    const index = this.references.findIndex(
      (ref) => normalizeDocumentId(ref.id) === normalizedDocumentId
    );
    if (index !== -1) {
      this.references[index] = reference;
    }
  }

  @Mutation
  addList(list: R4.IList) {
    this.lists.push(list);
  }

  @Mutation
  setDocuments(entries: Map<string, IDocumentReferenceExtended>) {
    this.entries = entries;
    this.entriesVersion++;
  }

  @Mutation
  removeReference(reference: R4.IDocumentReference) {
    this.references = this.references.filter((ref) => ref.id !== reference.id);
    this.entriesVersion++;
  }

  @Mutation
  removeEntry(document: IDocumentReferenceExtended) {
    this.entries.delete(`${document.homeCommunityId}-${document.id}`);
    this.entriesVersion++;
  }

  @Action
  private setEntries() {
    if (this.references.length != 0) {
      const fetchedEntries: IDocumentReferenceExtended[] = structuredClone(this.references);

      let localEntries = new Map<string, IDocumentReferenceExtended>(
        fetchedEntries.map((entry) => {
          const homeCommunityID = getHomeCommunityId(entry);

          entry.homeCommunityId = homeCommunityID!;
          const key = homeCommunityID + "-" + entry.id!;
          return [key, entry];
        })
      );

      localEntries = mapLocalEntries(this.lists, localEntries);

      this.setRelationshipTree(new Tree(fetchedEntries));

      localEntries.forEach((ref) => {
        const logicalEntry = ref.extension?.find(
          (ext) => ext.url == "https://api.phellowseven.com/fhir/StructureDefinition/logicalEntry"
        )?.valueReference;

        ref.originalSubmissionSet = this.lists.find(
          (set) =>
            set.entry?.map((entry) => entry.item?.reference)?.indexOf(logicalEntry?.reference) != -1
        );

        const id = ref.homeCommunityId + "-" + ref.id!;
        ref.version = this.relationshipTree.getVersion(id) || 1;
        ref.addendumNumber = this.relationshipTree.getAddendumNumber(id) || 1;
      });

      this.setLoading(false);
      this.setDocuments(localEntries);

      const leafEntries = this.relationshipTree
        .getLeaves()
        .map((node) => {
          const nested = this.relationshipTree
            .getParents(node)
            .map((nestedNode) => {
              const entry = this.entries.get(nestedNode.id);
              if (entry) {
                if (nestedNode.directChild) {
                  entry.relationship = {
                    type: nestedNode.type,
                    relatedNode: nestedNode.directChild,
                  };
                }
              }
              return entry;
            })
            .filter((entry) => entry) as IDocumentReferenceExtended[];
          const entry = this.entries.get(node.id) as IDocumentReferenceExtended;
          entry.nested = nested;
          return entry;
        })
        .filter((entry) => entry) as IDocumentReferenceExtended[];
      this.setLeafEntries(leafEntries);
    } else {
      this.setDocuments(new Map());
      this.setRelationshipTree(new Tree(null));
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addNewEntry(bundle: R4.IBundle) {
    this.setRelationshipTree(new Tree(null));
    const tempDocRef = bundle.entry![1].resource as IDocumentReferenceExtended & {
      temp: boolean;
    };

    tempDocRef.id = (bundle.entry![1].fullUrl?.split("/").pop() || "").replace("urn:uuid:", "");
    tempDocRef.temp = true;

    this.addReference(tempDocRef);

    const list = bundle.entry![0].resource as R4.IList;
    list.entry![0].item.reference = `DocumentReference/${tempDocRef.id}`;
    this.addList(list);

    const homeCommunityID = getHomeCommunityId(tempDocRef);

    if (tempDocRef.relatesTo?.[0].code === R4.DocumentReference_RelatesToCodeKind._replaces) {
      const relatedDocRefId = tempDocRef.relatesTo?.[0].target.reference?.split("/").pop();
      tempDocRef.homeCommunityId = homeCommunityID!;

      const relatedDocRefIndex = this.references.findIndex((ref) => ref.id === relatedDocRefId);
      if (relatedDocRefIndex !== -1) {
        const relatedDocRef = this.references[relatedDocRefIndex];
        this.updateReference({
          ...relatedDocRef,
          status: R4.DocumentReferenceStatusKind._superseded,
        });
      }
    }

    this.setEntries();

    const auth = await authHeaderFunction();
    if (auth == null) {
      throw new Error("Failed to get authorization");
    }

    // NOTE: async fetch of the document reference to get the full resource
    const crossCommunityId = btoa(`urn:uuid:${tempDocRef.id}@urn:oid:${homeCommunityID}`);
    const ref = await fetchDocumentReference(crossCommunityId, auth);
    this.updateReference(ref);
    this.setEntries();
  }

  @Action({ rawError: true })
  replaceEntry(bundle: R4.IBundle) {
    this.setRelationshipTree(new Tree(null));

    const newReference = bundle.entry![1].resource as IDocumentReferenceExtended;
    newReference.homeCommunityId = getHomeCommunityId(newReference)!;
    this.addReference(newReference);

    const oldReferenceId = bundle.entry![1].request!.url!.split("/").pop() || "";
    const oldReference = this.references.find(
      (ref) => ref.id === oldReferenceId
    ) as IDocumentReferenceExtended;
    if (oldReference) {
      this.removeReference(oldReference!);
      this.removeEntry(oldReference!);
    }

    const list = bundle.entry![0].resource as R4.IList;
    list.entry![0].item.reference = `DocumentReference/${newReference.id}`;
    this.addList(list);

    this.setEntries();
  }

  @Action
  removeDocument(document: IDocumentReferenceExtended) {
    this.removeEntry(document);
    this.removeReference(document);
  }

  get documents(): IDocumentReferenceExtended[] {
    // Makes the getter reactive despite the underlying entries Map not being reactive in Vue 2
    this.entriesVersion;
    return Array.from(this.entries.values());
  }

  get fetched(): boolean {
    return this.entries.size > 0;
  }

  @Action({ rawError: true })
  async fetchDocuments() {
    this.setLoading(true);

    const references: R4.IDocumentReference[] = [];
    const lists: R4.IList[] = [];

    try {
      const auth = await authHeaderFunction();
      if (auth === null) {
        throw new Error("Failed to get authorization");
      }

      // Helper function to process bundle entries
      const processBundleEntries = (bundle: R4.IBundle) => {
        if (!bundle.entry) return;
        for (const entry of bundle.entry) {
          if (entry.resource?.resourceType === "DocumentReference") {
            references.push(entry.resource as R4.IDocumentReference);
          } else if (entry.resource?.resourceType === "List") {
            lists.push(entry.resource as R4.IList);
          }
        }
      };

      // Fetch the first page and follow pagination using a do-while loop
      let nextUrl: string | undefined = undefined;
      let bundle: R4.IBundle | undefined = undefined;
      do {
        bundle = await fetchDocuments(auth, nextUrl);
        processBundleEntries(bundle);
        this.setLists(lists);
        this.setReferences(references);
        this.setEntries();
        nextUrl = bundle.link?.find((l: any) => l.relation === "next")?.url;
      } while (nextUrl);
    } catch (e) {
      this.setLists(lists);
      this.setReferences(references);
      this.setEntries();
      // TODO: rethrow error?
      throw e;
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async fetchDocument(id: string) {
    this.setLoading(true);

    try {
      const auth = await authHeaderFunction();
      if (auth === null) {
        throw new Error("Failed to get authorization");
      }
      const reference = await fetchDocumentReference(id, auth);
      this.addReference(reference);
    } finally {
      this.setLoading(false);
    }

    this.setEntries();
  }

  @Action({ rawError: true })
  async deleteDocument(entry: IDocumentReferenceExtended) {
    try {
      this.removeDocument(entry!);
      const authorization = await authHeaderFunction();
      if (!authorization) {
        throw new Error("Failed to get authorization");
      }
      await deleteDocument(entry, authorization);
    } catch (e) {
      await this.fetchDocuments();
      throw new Error("Failed to delete document");
    }
  }

  @Action({ rawError: true })
  async updateDocumentStatus({
    document,
    status,
  }: {
    document: R4.IDocumentReference;
    status: R4.DocumentReferenceStatusKind;
  }) {
    const authorization = await authHeaderFunction();
    if (!authorization) {
      throw new Error("Failed to get authorization");
    }
    await updateDocumentStatus(document, status, authorization);
    await this.fetchDocuments(); // TODO: update state instead
  }
}

export interface IDocumentReferenceRelationship {
  type: R4.DocumentReference_RelatesToCodeKind | null;
  relatedNode: DirectNodeChild;
}

export interface IDocumentReferenceExtended extends R4.IDocumentReference {
  submissionSet?: R4.IList;
  originalSubmissionSet?: R4.IList;
  version?: number;
  addendumNumber?: number;
  homeCommunityId: string;
  nested?: IDocumentReferenceExtended[];
  relationship?: IDocumentReferenceRelationship;
  temp?: boolean;
}

export interface IDocumentAttachment {
  metadata: IDocumentReferenceExtended;

  content(): Promise<Blob>;
  objectURL(): Promise<string>;
}

export class DocumentAttachment implements IDocumentAttachment {
  metadata: IDocumentReferenceExtended;

  private accountModule: AccountModule;
  private _content: Blob | null = null;

  constructor(metadata: IDocumentReferenceExtended, accountModule: AccountModule) {
    this.accountModule = accountModule;
    this.metadata = metadata;
  }

  async content(): Promise<Blob> {
    if (!this._content) await this.download();

    return this._content!;
  }

  async objectURL(): Promise<string> {
    return URL.createObjectURL(await this.content());
  }

  private async download() {
    const url = this.metadata.content[0].attachment.url!;

    const auth = await authHeaderFunction();
    if (!auth) {
      throw new UnauthorizedError("Failed to get authorization");
    }

    const response = await window.fetch(url, {
      method: "GET",
      headers: {
        Authorization: auth,
      },
    });

    this._content = await response.blob();
  }
}

export class ChCdaDocumentAttachment implements IDocumentAttachment {
  attachment: IDocumentAttachment;

  private accountState: AccountModule;
  private _objectURL: string | null = null;
  private language?: string;

  constructor(attachment: IDocumentAttachment, accountState: AccountModule, language?: string) {
    this.attachment = attachment;
    this.accountState = accountState;
    this.language = language;
  }

  get metadata(): IDocumentReferenceExtended {
    return this.attachment.metadata;
  }

  content(): Promise<Blob> {
    return this.attachment.content();
  }

  async objectURL(): Promise<string> {
    if (this._objectURL) return this._objectURL;

    const content = await this.content();
    if (content.type.indexOf("xml") === -1) {
      this._objectURL = URL.createObjectURL(content);
      return this._objectURL;
    }

    const html = await this.convertToHtml();
    this._objectURL = URL.createObjectURL(html);
    return this._objectURL;
  }

  async convertToPdf(): Promise<Blob> {
    return await this.convertTo("application/pdf");
  }

  async convertToHtml(): Promise<Blob> {
    return await this.convertTo("text/html");
  }

  private async convertTo(targetMimeType: string): Promise<Blob> {
    const xml = await this.content();

    // XLST Transform Service
    let conversionUrl = config.eprServices.conversion;

    if (this.language) {
      conversionUrl += `?locale=${this.language}`;
    }

    const response = await window.fetch(conversionUrl, {
      method: "POST",
      headers: {
        Authorization: await this.accountState.getAuthenticationHeader(),
        "Content-Type": `text/xml; format=${this.metadata.content[0].format!.code}`,
        Accept: targetMimeType,
      },
      body: xml,
    });
    return await response.blob();
  }

  async objectURL2(): Promise<string> {
    if (this._objectURL) return this._objectURL;

    const content = await this.content();
    if (content.type.indexOf("xml") == -1) {
      this._objectURL = URL.createObjectURL(content);
      return this._objectURL;
    }

    const xml = await content.text();

    const replaced = xml.replace(
      /<\?xml(.+)\?>/,
      `<?xml$1?>\n<?xml-stylesheet type="text/xsl" href="${window.location.origin}/xsl/cda-ch/cda-ch.xsl" ?>`
    );

    const blob = new Blob([new TextEncoder().encode(replaced)], {
      type: "text/xml",
    });

    this._objectURL = URL.createObjectURL(blob);
    return this._objectURL;
  }
}

export class ChFhirDocumentAttachment implements IDocumentAttachment {
  attachment: IDocumentAttachment;

  private accountState: AccountModule;
  private _objectURL: string | null = null;
  private language?: string;

  constructor(attachment: IDocumentAttachment, accountState: AccountModule, language?: string) {
    this.attachment = attachment;
    this.accountState = accountState;
    this.language = language;
  }

  get metadata(): IDocumentReferenceExtended {
    return this.attachment.metadata;
  }

  content(): Promise<Blob> {
    return this.attachment.content();
  }

  async objectURL(): Promise<string> {
    if (this._objectURL) return this._objectURL;

    const content = await this.content();
    if (content.type.indexOf("json") === -1 && content.type.indexOf("xml") === -1) {
      this._objectURL = URL.createObjectURL(content);
      return this._objectURL;
    }

    const html = await this.convertToHtml();
    this._objectURL = URL.createObjectURL(html);
    return this._objectURL;
  }

  async convertToPdf(): Promise<Blob> {
    return await this.convertTo("application/pdf");
  }

  async convertToHtml(): Promise<Blob> {
    return await this.convertTo("text/html");
  }

  private async convertTo(targetMimeType: string): Promise<Blob> {
    const content = await this.content();

    // Transform to FHIR XML
    let xml;
    if (content.type.indexOf("json") !== -1) {
      const response = await window.fetch(config.r4.documents.upload + "$convert", {
        method: "POST",
        headers: {
          Authorization: await this.accountState.getAuthenticationHeader(),
          Accept: "application/fhir+xml",
          "Content-Type": "application/fhir+json",
        },
        body: content,
      });

      xml = await response.text();
    } else {
      xml = await this.content();
    }

    // XLST Transform Service
    let conversionUrl = config.eprServices.conversion;
    if (this.language) {
      conversionUrl += `?locale=${this.language}`;
    }
    const response = await window.fetch(conversionUrl, {
      method: "POST",
      headers: {
        Authorization: await this.accountState.getAuthenticationHeader(),
        "Content-Type": `application/fhir+xml; format=${this.metadata.content[0].format!.code}`,
        Accept: targetMimeType,
      },
      body: xml,
    });
    return await response.blob();
  }
}
